import React, { useState, useCallback, useEffect, useRef, memo } from 'react';
import { flushSync } from 'react-dom';
import { NodeProps, useReactFlow, useUpdateNodeInternals, Node } from '@xyflow/react';
import { BaseNode } from './BaseNode';
import { renderComponent, ComponentType, FileDropZone, FileContextIndicator, LoadingOverlay, NodeSettingsFooter } from '../ui';
import { TuneIcon } from './nodeIcons';
import logger from '../../utils/logger';
import InpaintEditor from '../inpaint/InpaintEditor';
import { getInputHandlesForDefinitionType, getOutputNodeDimensions, isFirstLastFrameCapable, modelSupportsFirstLastFrame } from '../../config/nodeRegistry';
import { alertService } from '../Alert';
import { VideoTimelinePreview } from '../ui/VideoTimelinePreview';
import fileStorageService from '../../services/FileStorageService';
import flowStorageService from '../../services/FlowStorageService';
import { useAutoSaveContext } from '../../contexts/AutoSaveContext';
import { recordUserAction } from '../../services/FlowHistoryRef';
import { geminiOmniPreviewNotice } from '../../../server/config/geminiOmniRetirement';

// Monotonic counter to ensure unique node IDs
let _uNodeIdCounter = 0;
function uniqueNodeId(prefix: string): string {
  return `${prefix}-${Date.now()}-${_uNodeIdCounter++}`;
}

const BANNER_PRESETS: Record<string, { width: number; height: number }> = {
  'google-300x250': { width: 300, height: 250 },
  'google-336x280': { width: 336, height: 280 },
  'google-728x90': { width: 728, height: 90 },
  'google-970x90': { width: 970, height: 90 },
  'google-300x600': { width: 300, height: 600 },
  'google-160x600': { width: 160, height: 600 },
  'google-320x50': { width: 320, height: 50 },
  'google-320x100': { width: 320, height: 100 },
  'meta-1080x1080': { width: 1080, height: 1080 },
  'meta-1080x1350': { width: 1080, height: 1350 },
  'meta-1080x1920': { width: 1080, height: 1920 },
  'social-1200x628': { width: 1200, height: 628 },
  'linkedin-1200x627': { width: 1200, height: 627 },
};

export interface SettingsUpdate {
  updatedParams: Record<string, any>;
  topLevelUpdates: Record<string, any>;
}

/**
 * Compute what a settings-footer / prompt-modal-toolbar write does to a node's
 * persisted data. Pure: same inputs, same output, no React state touched.
 *
 * Extracted out of `handleSettingsChange` so the "did this actually change
 * anything?" question can be answered *before* the write is applied. The
 * setter still calls this again inside the `setNodes` updater against the
 * freshest node — one implementation, two callers, so the dirty decision can
 * never drift from the write it is describing.
 */
export function computeSettingsUpdate(
  definitionType: string,
  nodeData: Record<string, any>,
  fieldId: string,
  value: any
): SettingsUpdate {
  const currentParams = (nodeData.parameters || {}) as Record<string, any>;
  let updatedParams: Record<string, any> = {
    ...currentParams,
    [fieldId]: value,
  };

  if (fieldId === 'bannerPreset') {
    const presetKey = String(value || '');
    const preset = BANNER_PRESETS[presetKey];
    if (preset) {
      updatedParams = {
        ...updatedParams,
        aspectRatio: 'custom',
        customWidth: preset.width,
        customHeight: preset.height,
      };
    }
  }

  if (fieldId === 'aspectRatio' && value !== 'custom') {
    updatedParams.bannerPreset = 'none';
  }

  // ── ImageScale preset ↔ W/H sync ──
  if (definitionType === 'ImageScale') {
    const SCALE_SHORT_SIDES: Record<string, number> = {
      '720p': 720, '1080p': 1080, '2k': 1440, '4k': 2160,
    };
    const SCALE_RATIOS: Record<string, [number, number]> = {
      '16:9': [16, 9], '9:16': [9, 16], '1:1': [1, 1],
      '4:5': [4, 5], '5:4': [5, 4], '4:3': [4, 3], '3:4': [3, 4],
    };
    const inputW = (nodeData.inputWidth as number) || 1920;
    const inputH = (nodeData.inputHeight as number) || 1080;

    if (fieldId === 'aspectRatio' || fieldId === 'resolution') {
      const ar = String(updatedParams.aspectRatio ?? 'original');
      const res = String(updatedParams.resolution ?? 'original');

      // Determine the effective aspect ratio
      let ratioW: number;
      let ratioH: number;
      if (ar === 'original' || ar === 'custom') {
        ratioW = inputW; ratioH = inputH;
      } else {
        const r = SCALE_RATIOS[ar];
        if (r) { ratioW = r[0]; ratioH = r[1]; }
        else { ratioW = inputW; ratioH = inputH; }
      }

      // Determine the short-side pixel count
      let shortSide: number;
      if (res === 'original' || res === 'custom') {
        shortSide = Math.min(inputW, inputH);
      } else {
        shortSide = SCALE_SHORT_SIDES[res] || Math.min(inputW, inputH);
      }

      // Compute target W/H
      const arValue = ratioW / ratioH;
      let tw: number;
      let th: number;
      if (arValue >= 1) {
        th = shortSide;
        tw = Math.round(shortSide * arValue);
      } else {
        tw = shortSide;
        th = Math.round(shortSide / arValue);
      }
      // Even dimensions
      if (tw % 2 !== 0) tw += 1;
      if (th % 2 !== 0) th += 1;

      updatedParams.targetWidth = tw;
      updatedParams.targetHeight = th;
    }

    if (fieldId === 'targetWidth' || fieldId === 'targetHeight') {
      const tw = Number(updatedParams.targetWidth) || 1920;
      const th = Number(updatedParams.targetHeight) || 1080;
      const ratio = tw / th;

      // Reverse-match aspect ratio (1% tolerance)
      let matchedAR = 'custom';
      if (Math.abs(ratio - inputW / inputH) < 0.01) {
        matchedAR = 'original';
      } else {
        for (const [key, [rw, rh]] of Object.entries(SCALE_RATIOS)) {
          if (Math.abs(ratio - rw / rh) < 0.01) { matchedAR = key; break; }
        }
      }
      updatedParams.aspectRatio = matchedAR;

      // Reverse-match resolution
      const shortSide = Math.min(tw, th);
      let matchedRes = 'custom';
      if (shortSide === Math.min(inputW, inputH)) {
        matchedRes = 'original';
      } else {
        for (const [key, ss] of Object.entries(SCALE_SHORT_SIDES)) {
          if (shortSide === ss) { matchedRes = key; break; }
        }
      }
      updatedParams.resolution = matchedRes;
    }
  }

  // ── VeoVideo: keep the settings inside what Google will render ──
  // Google renders 1080p/4k only at 8s, Lite has no 4k and no first/last
  // frame. The server refuses those combinations (400, before reserving);
  // this keeps the node from ever holding one, so the quote the node shows is
  // a request the server will accept and bill at that same price.
  if (definitionType === 'VeoVideo') {
    const EIGHT_SECOND_ONLY = new Set(['1080p', '4k']);
    const model = fieldId === 'selectedModel'
      ? value
      : (nodeData.selectedModel ?? currentParams.selectedModel);
    const isLite = model === 'veo-3.1-lite-generate-preview';
    const res = () => String(updatedParams.resolution ?? '').toLowerCase();

    // Picking 1080p/4k pulls the duration to 8s…
    if (fieldId === 'resolution' && EIGHT_SECOND_ONLY.has(res())) {
      updatedParams.duration = '8';
    }
    // …and picking a shorter duration at 1080p/4k drops the resolution to 720p.
    if (fieldId === 'duration' && String(value) !== '8' && EIGHT_SECOND_ONLY.has(res())) {
      updatedParams.resolution = '720p';
    }
    if (isLite) {
      // 4k → 1080p (duration is already 8s, since 4k required it).
      if (res() === '4k') updatedParams.resolution = '1080p';
      if (updatedParams.mode === 'first-last-frame') updatedParams.mode = 'image-to-video';
    }
  }

  // selectedModel lives at data.selectedModel (top-level), not inside parameters
  const topLevelUpdates: Record<string, any> = {};
  if (fieldId === 'selectedModel') {
    topLevelUpdates.selectedModel = value;
  }

  // ── Manual aspect-ratio override ──────────────────────────────────
  // The connected-input auto-select in NodeAdapters must never overwrite
  // a ratio the user chose. Record the choice here: the settings footer
  // and the prompt modal's inline toolbar both land in this one setter,
  // so this is the only place a user-driven aspectRatio write happens.
  // Indirect writes count as a choice too — picking a banner preset
  // forces aspectRatio to 'custom', and editing an ImageScale target
  // width/height reverse-matches it above.
  if (fieldId === 'aspectRatio' || updatedParams.aspectRatio !== currentParams.aspectRatio) {
    topLevelUpdates.aspectRatioManuallySet = true;
    topLevelUpdates.aspectRatioAutoSelected = false;
  }

  return { updatedParams, topLevelUpdates };
}

/**
 * Would applying this {@link SettingsUpdate} to `nodeData` leave the persisted
 * data byte-for-byte the same? Used to keep a no-op interaction — re-clicking
 * the option that is already selected, or a select that re-fires with its own
 * value — from marking the flow dirty and rewriting the stored graph.
 *
 * Shallow by design: every value a settings field writes is a primitive
 * (string / number / boolean), so `Object.is` per key is the right depth.
 */
export function settingsUpdateIsNoOp(
  nodeData: Record<string, any>,
  { updatedParams, topLevelUpdates }: SettingsUpdate
): boolean {
  const prevParams = (nodeData.parameters || {}) as Record<string, any>;
  const keys = new Set([...Object.keys(prevParams), ...Object.keys(updatedParams)]);
  for (const key of keys) {
    if (!Object.is(prevParams[key], updatedParams[key])) return false;
  }
  for (const key of Object.keys(topLevelUpdates)) {
    if (!Object.is(nodeData[key], topLevelUpdates[key])) return false;
  }
  return true;
}

/**
 * Universal Node Definition
 * JSON-driven node configuration
 */
export interface UniversalNodeDefinition {
  // Node metadata
  id: string;
  type: string;
  title: string;
  category: 'source' | 'processor' | 'output' | 'container';
  variant: 'positive' | 'negative' | 'neutral' | 'purple' | 'warning' | 'orange';
  hidden?: boolean; // If true, hide from palette and context menu
  alwaysShowSettings?: boolean; // If true, settings are always visible (for utility nodes with no textarea)
  
  // Media configuration
  acceptsMediaTypes?: string[]; // e.g., ['text', 'image', 'video']
  outputsMediaType?: string;
  maxInputConnections?: number;
  
  // Model configuration
  fixedModel?: string; // Lock node to a specific model (e.g., 'veo-3.1-fast-generate-preview')
  
  // UI Components
  components: UniversalNodeComponent[];
  
  // Node behavior
  hasInputHandle?: boolean;
  hasOutputHandle?: boolean;
  
  // Settings panel configuration (for generator nodes)
  settings?: {
    id: string;
    label: string;
    type: 'select' | 'toggle' | 'slider' | 'model' | 'boolean' | 'number' | 'anchor';
    options?: Array<{ value: string; label: string }>;
    models?: Array<{ id: string; name: string; description?: string }>;
    min?: number;
    max?: number;
    step?: number;
    infoTooltip?: string;
    description?: string;
    default?: string | boolean | number;
    hidden?: boolean;
    conditionalOptions?: {
      dependsOn: string;
      rules: Record<string, Array<{ value: string; label: string }>>;
    };
    showWhen?: {
      field: string;
      value?: string | number | boolean;
      operator?: '>' | '<' | '=' | '!=' | '>=' | '<=';
    };
  }[];
  
  // Custom node metadata (for migrated custom nodes)
  _customNodeMeta?: {
    icon: string;
    color: string;
    inputs: any[];
    outputs: any[];
    originalDefinition?: any;
  };
}

export interface UniversalNodeComponent {
  id: string;
  type: ComponentType;
  props: Record<string, any>;
  
  // Data binding
  valueKey?: string; // Key in node.data to bind to
  onChangeKey?: string; // Key in node.data for change handler
  onSubmitKey?: string; // Key in node.data for submit handler
}

/**
 * Extended data interface for UniversalNode
 */
interface UniversalNodeData {
  definition?: UniversalNodeDefinition;
  pages?: Array<{ content: string; type?: string; timestamp?: string | number; context?: unknown }>;
  currentPageIndex?: number;
  status?: 'idle' | 'processing' | 'success' | 'error' | 'warning';
  statusMessage?: string;
  type?: string;
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  fileContexts?: unknown[];
  onFilesDropped?: (files: File[]) => void;
  [key: string]: unknown;
}

/**
 * Universal Node Component
 * Renders any node type based on JSON definition
 * Memoized to prevent unnecessary re-renders when other nodes change
 */
const UniversalNodeComponent: React.FC<NodeProps> = ({ id, data: rawData, selected }) => {
  const data = rawData as UniversalNodeData;
  const definition = data.definition as UniversalNodeDefinition;
  const [inpaintOpen, setInpaintOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [inpaintImageUrl, _setInpaintImageUrl] = useState<string>('');
  
  const hasSettings = definition?.settings && definition.settings.length > 0;
  const alwaysShowSettings = !!definition?.alwaysShowSettings;
  // Settings start closed (unless the node always shows them) and open only via
  // explicit user interaction. Local state means virtualization remounts reset
  // to closed, which is the desired behavior for non-alwaysShowSettings nodes.
  const [isSettingsOpen, setIsSettingsOpen] = useState(alwaysShowSettings);
  
  const { setNodes, setEdges, getNode, fitView } = useReactFlow();
  const { markDirty } = useAutoSaveContext();

  // Toggle API Output pill on output nodes
  const handleToggleApiOutput = useCallback(() => {
    recordUserAction('Toggle API output');
    setNodes((nodes) => nodes.map((n) => {
      if (n.id !== id) return n;
      const isEnabled = !n.data.apiOutput;
      // Auto-generate label if enabling and no label exists
      let label = n.data.apiOutputLabel as string | undefined;
      if (isEnabled && !label) {
        // Count how many output nodes already have apiOutput enabled
        const existingCount = nodes.filter(
          (nd) => nd.id !== id && nd.data.apiOutput
        ).length;
        label = `output_${existingCount + 1}`;
      }
      return {
        ...n,
        data: { ...n.data, apiOutput: isEnabled, apiOutputLabel: isEnabled ? label : n.data.apiOutputLabel },
      };
    }));
    markDirty(); // The publish schema is built from this flag — it has to persist.
  }, [id, setNodes, markDirty]);

  // Update API Output label
  const handleApiOutputLabelChange = useCallback((newLabel: string) => {
    recordUserAction('Rename', { coalesceKey: `text:${id}:apiOutputLabel` });
    setNodes((nodes) => nodes.map((n) => {
      if (n.id !== id) return n;
      return { ...n, data: { ...n.data, apiOutputLabel: newLabel } };
    }));
    markDirty();
  }, [id, setNodes, markDirty]);

  // Toggle API Input pill on input container nodes (ImagePanel, DocumentPanel).
  // Mirrors handleToggleApiOutput. Auto-generates `input_N` labels.
  const handleToggleApiInput = useCallback(() => {
    recordUserAction('Toggle API input');
    setNodes((nodes) => nodes.map((n) => {
      if (n.id !== id) return n;
      const isEnabled = !n.data.apiInput;
      let label = n.data.apiInputLabel as string | undefined;
      if (isEnabled && !label) {
        const existingCount = nodes.filter(
          (nd) => nd.id !== id && nd.data.apiInput
        ).length;
        label = `input_${existingCount + 1}`;
      }
      return {
        ...n,
        data: { ...n.data, apiInput: isEnabled, apiInputLabel: isEnabled ? label : n.data.apiInputLabel },
      };
    }));
    markDirty(); // The publish schema is built from this flag — it has to persist.
  }, [id, setNodes, markDirty]);

  // Update API Input label
  const handleApiInputLabelChange = useCallback((newLabel: string) => {
    recordUserAction('Rename', { coalesceKey: `text:${id}:apiInputLabel` });
    setNodes((nodes) => nodes.map((n) => {
      if (n.id !== id) return n;
      return { ...n, data: { ...n.data, apiInputLabel: newLabel } };
    }));
    markDirty();
  }, [id, setNodes, markDirty]);

  // Close settings panel when generation starts (never for always-visible utility nodes)
  useEffect(() => {
    if (alwaysShowSettings) return;
    if (data.isProcessing && isSettingsOpen) {
      setIsSettingsOpen(false);
    }
  }, [alwaysShowSettings, data.isProcessing]);

  // React Flow caches each node's handle geometry in `internals.handleBounds` and
  // only re-measures on a DOM resize, a remount, or an explicit
  // `updateNodeInternals` call. Our handle SET is data-driven (FFLF `mode`,
  // VideoStitch `clipCount`) while nodes carry a fixed inline width/height and
  // handles are absolutely positioned — so the observed border-box never changes
  // and the ResizeObserver never fires. Without this the new <Handle>s render but
  // stay unregistered: no snapping, no hover highlight, and edges anchored to them
  // fail to render (React Flow error 008). Keyed on the handle-id SIGNATURE so it
  // fires exactly when the handle set changes, never on every render.
  const updateNodeInternals = useUpdateNodeInternals();
  const inputHandleSignature = definition
    ? getInputHandlesForDefinitionType(definition.type, data).map((h) => h.id).join('|')
    : '';
  useEffect(() => {
    if (!inputHandleSignature) return;
    updateNodeInternals(id);
  }, [id, inputHandleSignature, updateNodeInternals]);

  // Reconcile a stored `mode` the selected model cannot execute. First-to-Last
  // Frame used to be selectable on every Kling tier even though only 3.0 has an
  // FFLF path (see modelSupportsFirstLastFrame). The option is now gated per
  // model, but flows saved before the gate — and any switch down from 3.0 while
  // FFLF is selected — would otherwise keep a mode that reaches the provider as
  // a text-to-video call carrying frame params, i.e. a failed generation with
  // credits already reserved. Reset to Image-to-Video and collapse the frame
  // edges exactly as leaving FFLF by hand does, and say so instead of silently
  // changing what the user configured.
  const fflfParams = (data.parameters || {}) as Record<string, unknown>;
  const fflfSelectedModel =
    (fflfParams.selectedModel as string | undefined) || (data.selectedModel as string | undefined);
  const fflfModelMismatch =
    !!definition &&
    isFirstLastFrameCapable(definition.type) &&
    fflfParams.mode === 'first-last-frame' &&
    !modelSupportsFirstLastFrame(fflfSelectedModel);
  // Latch so StrictMode's double-invoke (and any re-render before the write
  // lands) warns once; cleared as soon as the mismatch is gone, so a later
  // 3.0 → 2.6 switch reconciles again.
  const fflfReconciledRef = useRef(false);
  useEffect(() => {
    if (!fflfModelMismatch) {
      fflfReconciledRef.current = false;
      return;
    }
    if (fflfReconciledRef.current) return;
    fflfReconciledRef.current = true;
    const modelLabel =
      definition?.settings
        ?.find((f) => f.id === 'selectedModel')
        ?.models?.find((m) => m.id === fflfSelectedModel)?.name || fflfSelectedModel;
    logger.warn(
      `[UniversalNode] ${definition?.type} ${fflfSelectedModel} has no first-last-frame support — resetting mode to image-to-video`,
      { nodeId: id }
    );
    setNodes((nodes) => nodes.map((n) => {
      if (n.id !== id) return n;
      const currentParams = (n.data.parameters || {}) as Record<string, unknown>;
      if (currentParams.mode !== 'first-last-frame') return n;
      return {
        ...n,
        data: { ...n.data, parameters: { ...currentParams, mode: 'image-to-video' } },
      };
    }));
    setEdges((edges) => edges.map((e) => {
      if (e.target !== id) return e;
      if (e.targetHandle === 'input-image-first' || e.targetHandle === 'input-image-last') {
        return { ...e, targetHandle: 'input-image' };
      }
      return e;
    }));
    alertService.warning(
      `${modelLabel} has no First-to-Last Frame mode — switched this node to Image-to-Video.`
    );
  }, [fflfModelMismatch, id, setNodes, setEdges]);

  if (!definition) {
    return (
      <BaseNode id={id} selected={selected} title="Error">
        <div style={{ color: '#EF4444' }}>Missing node definition</div>
      </BaseNode>
    );
  }

  // Handler for accepting inpaint result
  const handleInpaintAccept = useCallback((resultImageUrl: string) => {
    logger.debug('[UniversalNode] Inpaint accepted with result:', resultImageUrl);
    setInpaintOpen(false);
    
    // Get source node position
    const sourceNode = getNode(id);
    if (!sourceNode) {
      logger.error('[UniversalNode] Source node not found:', id);
      return;
    }

    // Calculate position for new output node (offset to the right)
    const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
    const newX = sourceNode.position.x + sourceWidth + 50;
    const newY = sourceNode.position.y;

    // Create new output node with proper structure
    const outputNodeId = `customOutput-${Date.now()}`;
    
    const initialPage = {
      content: resultImageUrl,
      context: null,
      type: 'image' as const,
      timestamp: new Date().toISOString()
    };
    
    const inpaintOutputDims = getOutputNodeDimensions('image');
    const outputNode: Node = {
      id: outputNodeId,
      type: 'universalOutput',
      position: { x: newX, y: newY },
      width: inpaintOutputDims.width,
      height: inpaintOutputDims.height,
      data: {
        output: resultImageUrl,
        content: resultImageUrl,
        type: 'image',
        outputsMediaType: 'image',
        imageUrl: resultImageUrl,
        pages: [initialPage],
        currentPageIndex: 0,
        streamingInProgress: false
      }
    };

    // Create edge from source to new output
    const edge = {
      id: `${id}-${outputNodeId}`,
      source: id,
      target: outputNodeId,
      type: 'image',
      animated: true,
      style: { stroke: '#42C3D7', strokeWidth: 3 }
    };

    // Add node and edge to canvas
    setNodes((nodes) => [...nodes, outputNode]);
    setEdges((edges) => [...edges, edge]);
    
    logger.debug('[UniversalNode] Created universalOutput node:', outputNodeId);
  }, [id, getNode, setNodes, setEdges]);

  // Handler for duplicating output as Image Container node
  const handleDuplicateOutput = useCallback((url: string, type: string) => {
    logger.debug('[UniversalNode] Duplicating output as Image Container:', { url, type });
    
    // Get source node position
    const sourceNode = getNode(id);
    if (!sourceNode) {
      logger.error('[UniversalNode] Source node not found:', id);
      return;
    }

    // Calculate position for new node (offset to the right)
    const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
    const newX = sourceNode.position.x + sourceWidth + 50;
    const newY = sourceNode.position.y;

    if (type === 'image') {
      // Create Image Container node
      const PANEL_WIDTH = 554;
      
      const newNode = {
        id: uniqueNodeId('imagePanel'),
        type: 'imagePanel',
        position: {
          x: newX,
          y: newY
        },
        width: PANEL_WIDTH,
        height: 400,
        data: {
          imageUrl: url,
          content: url,
          type: 'image'
        }
      };
      
      recordUserAction('Duplicate output');
      setNodes((nodes) => [...nodes, newNode]);
      logger.debug('[UniversalNode] Created duplicate Image Container node');
    } else if (type === 'video') {
      // Create Video Output node
      const outputNodeId = `customOutput-${Date.now()}`;
      
      const initialPage = {
        content: url,
        context: null,
        type: 'video' as const,
        timestamp: new Date().toISOString()
      };
      
      const duplicateOutputDims = getOutputNodeDimensions('video');
      const outputNode: Node = {
        id: outputNodeId,
        type: 'universalOutput',
        position: { x: newX, y: newY },
        width: duplicateOutputDims.width,
        height: duplicateOutputDims.height,
        data: {
          output: url,
          content: url,
          type: 'video',
          outputsMediaType: 'video',
          pages: [initialPage],
          currentPageIndex: 0,
          streamingInProgress: false
        }
      };

      recordUserAction('Duplicate output');
      setNodes((nodes) => [...nodes, outputNode]);
      logger.debug('[UniversalNode] Created duplicate Video Output node');
    } else if (type === 'audio') {
      // Same shape as the video branch — without this the "Duplicate" item in
      // the audio context menu was rendered but did nothing at all.
      const outputNodeId = `customOutput-${Date.now()}`;

      const initialPage = {
        content: url,
        context: null,
        type: 'audio' as const,
        timestamp: new Date().toISOString()
      };

      const duplicateOutputDims = getOutputNodeDimensions('audio');
      const outputNode: Node = {
        id: outputNodeId,
        type: 'universalOutput',
        position: { x: newX, y: newY },
        width: duplicateOutputDims.width,
        height: duplicateOutputDims.height,
        data: {
          output: url,
          content: url,
          type: 'audio',
          outputsMediaType: 'audio',
          pages: [initialPage],
          currentPageIndex: 0,
          streamingInProgress: false
        }
      };

      recordUserAction('Duplicate output');
      setNodes((nodes) => [...nodes, outputNode]);
      logger.debug('[UniversalNode] Created duplicate Audio Output node');
    }
  }, [id, getNode, setNodes]);

  // Handler for placing a video frame on the canvas as an Image Container node.
  // Mirrors the paste-image flow in CreativeNodeFlow: shows the data URL
  // immediately, uploads to R2 in the background, then swaps to the R2 URL
  // and dispatches the storage event so the asset panel refreshes. Without
  // this, the placed frame would never make it into the flow's persisted
  // state or into Flow Assets.
  const handlePlaceFrameOnStage = useCallback((dataUrl: string, blob: Blob) => {
    logger.debug('[UniversalNode] Placing video frame on stage');

    const sourceNode = getNode(id);
    if (!sourceNode) {
      logger.error('[UniversalNode] Source node not found:', id);
      return;
    }

    // Read intrinsic dimensions before creating the node so we can size it
    // to the frame's aspect ratio (matches the paste-image UX).
    const probeDims = (): Promise<{ width: number; height: number }> => new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({ width: 0, height: 0 });
      img.src = dataUrl;
    });

    probeDims().then((dims) => {
      const PANEL_WIDTH = 554;
      const HEADER_HEIGHT = 44;
      const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
      const newX = sourceNode.position.x + sourceWidth + 50;
      const newY = sourceNode.position.y;

      const fileName = `frame-${Date.now()}.png`;
      const fileType = blob.type || 'image/png';

      // Same data shape as a pasted image: rich context messages, file metadata,
      // preloaded dimensions for aspect-ratio locking, and an isUploading flag.
      const buildNodeData = (url: string, isUploading: boolean, fileId?: string) => ({
        imageUrl: url,
        content: url,
        type: 'image',
        context: {
          messages: [{
            role: 'user' as const,
            content: [
              { type: 'text' as const, text: 'Frame captured from video' },
              { type: 'image' as const, imageUrl: url, mimeType: fileType }
            ]
          }]
        },
        isUploading,
        fileId,
        fileName,
        fileType,
        preloadedWidth: dims.width,
        preloadedHeight: dims.height,
      });

      const aspectRatio = dims.width && dims.height ? dims.width / dims.height : 16 / 9;
      const nodeHeight = Math.round(PANEL_WIDTH / aspectRatio) + 100;
      const newNodeId = uniqueNodeId('imagePanel');
      const newNode = {
        id: newNodeId,
        type: 'imagePanel',
        position: { x: newX, y: newY - HEADER_HEIGHT / 2 },
        width: PANEL_WIDTH,
        height: nodeHeight,
        data: buildNodeData(dataUrl, true),
      };

      recordUserAction('Place frame');
      setNodes((nodes) => [...nodes, newNode]);

      // Upload to Flow Assets in the background. The node already renders the
      // data URL, so the user sees the frame instantly even if the upload
      // takes a beat.
      (async () => {
        try {
          await fileStorageService.init();
          const file = new File([blob], fileName, { type: fileType });
          const stored = await fileStorageService.uploadFile(file, {
            scope: 'flow',
            flowId: flowStorageService.getCurrentFlowId() || undefined,
            skipDeduplication: true,
          });
          setNodes((nodes) => nodes.map((n) =>
            n.id === newNodeId
              ? { ...n, data: { ...n.data, ...buildNodeData(stored.url, false, stored.id) } }
              : n
          ));
          // Tell the asset panel to refresh.
          window.dispatchEvent(new CustomEvent('fileStorageUpdated'));
          logger.info(`[UniversalNode] Frame saved to Flow Assets: ${fileName} (${stored.id})`);
        } catch (err) {
          logger.error('[UniversalNode] Failed to upload captured frame:', err);
          // Leave the data URL in place so the user still has the frame.
          setNodes((nodes) => nodes.map((n) =>
            n.id === newNodeId
              ? { ...n, data: { ...n.data, isUploading: false } }
              : n
          ));
        }
      })();
    });
  }, [id, getNode, setNodes]);

  // Handler for media metadata ready (aspect ratio detection)
  const handleMediaMetadataReady = useCallback((pageIndex: number, aspectRatio: number, width: number, height: number) => {
    // Only handle for Output nodes with image/video content
    if (definition.type !== 'Output') return;
    
    const currentNode = getNode(id);
    if (!currentNode) return;
    
    // Store the aspect ratio in page data
    const pages = (data.pages || []) as Array<{ content: string; context?: any; type: string; timestamp: string; aspectRatio?: number }>;
    if (pages[pageIndex] && !pages[pageIndex].aspectRatio) {
      // Update page with aspect ratio
      const updatedPages = [...pages];
      updatedPages[pageIndex] = { ...updatedPages[pageIndex], aspectRatio };
      
      // Calculate new node dimensions based on aspect ratio
      // Keep current width, adjust height to match aspect ratio
      const currentWidth = currentNode.measured?.width || currentNode.width || 320;
      // Account for header (~48px) + status bar (~20px) - padding removed for edge-to-edge media
      const contentAreaOffset = 68;
      const minHeight = 200; // Minimum height from BaseNode
      
      // Calculate ideal content height based on aspect ratio
      const idealContentHeight = currentWidth / aspectRatio;
      const idealNodeHeight = Math.max(minHeight, idealContentHeight + contentAreaOffset);
      
      // Update node dimensions with smooth transition
      setNodes((nodes) => nodes.map((n) => {
        if (n.id === id) {
          return {
            ...n,
            height: idealNodeHeight,
            data: {
              ...n.data,
              pages: updatedPages,
              currentAspectRatio: aspectRatio,
            },
          };
        }
        return n;
      }));
      
      logger.debug(`[UniversalNode] Media metadata detected for page ${pageIndex}: ${width}x${height} (AR: ${aspectRatio.toFixed(2)}), new height: ${idealNodeHeight.toFixed(0)}px`);
    }
  }, [definition.type, id, getNode, data.pages, setNodes]);

  // Settings panel handlers
  const handleSettingsClose = useCallback(() => {
    if (alwaysShowSettings) return; // Utility node settings are always visible
    setIsSettingsOpen(false);
  }, [alwaysShowSettings]);

  const handleSettingsToggle = useCallback(() => {
    setIsSettingsOpen(prev => {
      const willOpen = !prev;
      // When opening settings, zoom and center on this node
      if (willOpen) {
        const node = getNode(id);
        if (node) {
          fitView({
            nodes: [node],
            duration: 300,
            padding: 0.3,
            maxZoom: 1.2,
          });
        }
      }
      return willOpen;
    });
  }, [fitView, getNode, id]);

  const handleSettingsChange = useCallback((fieldId: string, value: any) => {
    // Decide *before* writing whether this interaction changes anything the
    // flow stores. `data` is this render's node data, which for a click on a
    // settings control is current. A footer select re-fires with its own value
    // when the user re-picks the option already selected; that is not an edit
    // and must not rewrite the saved graph.
    const preview = computeSettingsUpdate(definition.type, data as Record<string, any>, fieldId, value);
    if (!settingsUpdateIsNoOp(data as Record<string, any>, preview)) {
      // Every settings write — model, resolution, duration, aspect ratio —
      // lands here, from both the footer and the prompt modal's toolbar.
      // Without this the change lived only in canvas memory and the headless
      // API kept pricing and running the previously saved configuration.
      markDirty();
      // One undo step per setting; a slider scrub coalesces into one.
      recordUserAction(fieldId === 'selectedModel' ? 'Change model' : 'Change setting', { coalesceKey: `setting:${id}:${fieldId}` });
    }

    // Update the node's parameters with the new value
    setNodes((nodes) => nodes.map((n) => {
      if (n.id !== id) return n;
      const { updatedParams, topLevelUpdates } = computeSettingsUpdate(
        definition.type,
        n.data as Record<string, any>,
        fieldId,
        value
      );
      return {
        ...n,
        data: {
          ...n.data,
          ...topLevelUpdates,
          parameters: {
            ...updatedParams
          }
        }
      };
    }));

    // ── FFLF input-handle edge migration ──────────────────────────────────
    // Changing `mode` re-derives this node's input handles (single input-image
    // ⇄ input-image-first / input-image-last). Pre-existing edges must move with
    // the handle set — otherwise a legacy image edge is left dangling on a
    // handle that no longer renders and the FFLF adapter (which matches strictly
    // on input-image-first/last) can never collect it. Runs for both the footer
    // and the modal since they share this setter. FFLF-capable node types only.
    // `mode` can also change indirectly (VeoVideo: switching to Lite drops
    // first-last-frame), and the handle edges must follow either way.
    const prevMode = ((data.parameters || {}) as Record<string, any>).mode;
    const nextMode = fieldId === 'mode' ? value : preview.updatedParams.mode;
    if (fieldId === 'mode' || nextMode !== prevMode) {
      const isFflfCapable = isFirstLastFrameCapable(definition.type);
      if (isFflfCapable) {
        setEdges((edges) => {
          if (nextMode === 'first-last-frame') {
            // Entering FFLF: re-point the FIRST legacy image edge → first frame,
            // the SECOND → last frame. Any explicit input-image-first/last edge
            // already present keeps priority (its slot is treated as filled).
            const isLegacyImageHandle = (h: unknown) =>
              h == null || h === 'input' || h === 'input-any' || h === 'input-image';
            let firstTaken = edges.some(
              (e) => e.target === id && e.targetHandle === 'input-image-first'
            );
            let lastTaken = edges.some(
              (e) => e.target === id && e.targetHandle === 'input-image-last'
            );
            return edges.map((e) => {
              if (e.target !== id) return e;
              if (e.targetHandle === 'input-image-first' || e.targetHandle === 'input-image-last') return e;
              if (!isLegacyImageHandle(e.targetHandle)) return e;
              // Only migrate edges whose SOURCE node emits an image.
              const srcData = getNode(e.source)?.data as Record<string, any> | undefined;
              const emitsImage =
                srcData?.type === 'image' ||
                srcData?.definition?.outputsMediaType === 'image';
              if (!emitsImage) return e;
              if (!firstTaken) { firstTaken = true; return { ...e, targetHandle: 'input-image-first' }; }
              if (!lastTaken) { lastTaken = true; return { ...e, targetHandle: 'input-image-last' }; }
              return e;
            });
          }
          // Leaving FFLF: collapse any first/last image edges back onto the
          // single input-image handle. Two edges collapsing onto one handle is
          // acceptable — the I2V adapter consumes one and must not crash.
          return edges.map((e) => {
            if (e.target !== id) return e;
            if (e.targetHandle === 'input-image-first' || e.targetHandle === 'input-image-last') {
              return { ...e, targetHandle: 'input-image' };
            }
            return e;
          });
        });
      }
    }
  }, [data, definition, id, markDirty, setNodes, setEdges, getNode]);

  const handleSettingsGenerate = useCallback(() => {
    // Close settings panel and trigger onSubmit
    setIsSettingsOpen(false);
    if (data.onSubmit && typeof data.onSubmit === 'function') {
      (data.onSubmit as () => void)();
    }
  }, [data.onSubmit]);

  // Close settings when text editor modal opens (only for this node)
  const handleTextEditorOpen = useCallback((nodeId: string) => {
    // Only close settings if the modal belongs to this node
    if (nodeId === id && isSettingsOpen) {
      setIsSettingsOpen(false);
    }
  }, [id, isSettingsOpen]);

  // Get node type color for settings panel
  const getNodeTypeColor = useCallback(() => {
    switch (definition.outputsMediaType) {
      case 'text': return '#FED402';
      case 'image': return '#42C3D7';
      case 'video': return '#FF4B42';
      case 'audio': return '#10B981';
      default: return '#A2A2A2';
    }
  }, [definition.outputsMediaType]);

  // Get selected settings labels for display when settings are closed
  const getSelectedSettingsLabels = useCallback((): string[] => {
    if (!hasSettings || !definition.settings) return [];
    const params = (data.parameters || {}) as Record<string, any>;
    const labels: string[] = [];
    
    for (const field of definition.settings) {
      const value = params[field.id] ?? field.default;
      if (value === undefined || value === null) continue;
      
      // For select/model fields, find the label from options
      if (field.type === 'select' && field.options) {
        const option = field.options.find(opt => opt.value === value);
        if (option) labels.push(option.label);
      } else if (field.type === 'model' && field.models) {
        const model = field.models.find(m => m.id === value);
        if (model) labels.push(model.name);
      } else if (field.type === 'toggle' || field.type === 'boolean') {
        // For toggles, show label if enabled
        if (value === true) labels.push(field.label);
      }
    }
    return labels;
  }, [hasSettings, definition.settings, data.parameters]);

  // Note: hasSettings is already computed at top of component for initial state

  // Render each component in the definition
  const renderNodeComponents = () => {
    return definition.components.map((component) => {
      // Merge component props with data bindings
      const props = { ...component.props };
      
      // Disable components when node is processing
      if (data.isProcessing) {
        (props as any).disabled = true;
      }
      // Bind value if valueKey is specified
      if (component.valueKey && data[component.valueKey] !== undefined) {
        (props as any).value = data[component.valueKey];
      }
      
      // Bind onChange handler if onChangeKey is specified
      if (component.onChangeKey && data[component.onChangeKey]) {
        (props as any).onChange = data[component.onChangeKey];
      }
      
      // Bind onSubmit handler if onSubmitKey is specified
      if (component.onSubmitKey && data[component.onSubmitKey]) {
        (props as any).onSubmit = data[component.onSubmitKey];
      }
      
      // Pass creditCost to MarkdownTextarea for send button display
      if (component.type === 'MarkdownTextarea' && data.creditCost !== undefined) {
        (props as any).creditCost = data.creditCost;
      }

      // Pass usesOwnKey (BYOK) to MarkdownTextarea so it can show the 🔑 badge
      if (component.type === 'MarkdownTextarea' && data.usesOwnKey !== undefined) {
        (props as any).usesOwnKey = data.usesOwnKey;
      }
      
      // Pass hasConnectedTextInput to MarkdownTextarea to enable send button when text is connected
      if (component.type === 'MarkdownTextarea' && data.hasConnectedTextInput) {
        (props as any).hasConnectedTextInput = data.hasConnectedTextInput;
      }
      
      // Pass connectedTextContent to MarkdownTextarea to show as placeholder
      if (component.type === 'MarkdownTextarea' && data.connectedTextContent) {
        (props as any).connectedTextContent = data.connectedTextContent;
      }
      
      // Hide magic wand button (improve prompt feature temporarily disabled)
      if (component.type === 'MarkdownTextarea') {
        (props as any).showMagicWand = false;
      }
      
      // Pass variant color to MarkdownTextarea for modal styling
      if (component.type === 'MarkdownTextarea') {
        (props as any).variant = definition.variant;
        // Pass mediaOutputType for border color styling
        (props as any).mediaOutputType = definition.outputsMediaType as 'text' | 'image' | 'video' | undefined;
      }
      
      // Pass promptImprovementContext to MarkdownTextarea for AI improvement feature
      if (component.type === 'MarkdownTextarea') {
        // Determine content type based on node's outputsMediaType
        let contentType: 'image' | 'video' | 'text' = 'text';
        if (definition.outputsMediaType === 'image') {
          contentType = 'image';
        } else if (definition.outputsMediaType === 'video') {
          contentType = 'video';
        }
        
        // Pass context from connected nodes if available
        (props as any).promptImprovementContext = {
          contentType,
          selectedModel: data.selectedModel || undefined,
          upstreamContext: data.inputContext || undefined,
          imageContext: data.imageContext || undefined,
          imageUrls: data.imageUrls || undefined,
          nodeParameters: (data.parameters as Record<string, any>) || undefined,
        };
      }
      
      // Pass nodeTitle and onCondenseRequest to MarkdownTextarea for TextEditorModal
      if (component.type === 'MarkdownTextarea') {
        // Set modal title to node title
        (props as any).nodeTitle = definition.title ? `Edit ${definition.title}` : 'Edit Prompt';
        
        // Pass condense handler if available (will be set in NodeAdapters)
        if (data.onCondenseRequest) {
          (props as any).onCondenseRequest = data.onCondenseRequest;
        }
        
        // Close settings when text editor modal opens
        (props as any).onTextEditorOpen = handleTextEditorOpen;
        // Pass node ID to identify which node's modal opened
        (props as any).nodeId = id;
        
        // Enable "Accept & Generate" button for nodes that can generate
        // Check if node has onSubmit (generate capability) and is not currently processing
        if (data.onSubmit && !data.isProcessing) {
          (props as any).canGenerate = true;
          (props as any).onAcceptAndGenerate = (acceptedValue: string) => {
            // First, update the prompt value synchronously using flushSync
            // This ensures the node data is updated before onSubmit reads it
            const changeKey = component.onChangeKey;
            if (changeKey && typeof data[changeKey as keyof typeof data] === 'function') {
              flushSync(() => {
                (data[changeKey as keyof typeof data] as (val: string) => void)(acceptedValue);
              });
            }
            // Now call onSubmit - the prompt value will be up-to-date
            if (typeof data.onSubmit === 'function') {
              data.onSubmit(acceptedValue);
            }
          };
        }
      }
      
      // Hide MarkdownTextarea's built-in submit button when node has settings panel
      // The generate button is now at the bottom of the node (always visible)
      if (component.type === 'MarkdownTextarea' && hasSettings) {
        (props as any).showSubmitButton = false;
        // Pass isSettingsOpen for border-radius adjustment
        (props as any).isSettingsOpen = isSettingsOpen;
        // Only show settings toggle button when settings are closed
        if (!isSettingsOpen) {
          (props as any).extraButtons = (
            <button
              type="button"
              aria-label="Open settings"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                e.stopPropagation();
                handleSettingsToggle();
              }}
              onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
              className="settings-toggle-button nodrag"
              disabled={!!data.isProcessing}
              title="Open settings"
            >
              <TuneIcon size={18} />
            </button>
          );
        }
        // Pass settings fields/values for inline settings toolbar in the editor modal
        (props as any).settingsFields = definition.settings;
        (props as any).settingsValues = (data.parameters || {}) as Record<string, any>;
        (props as any).onSettingsChange = handleSettingsChange;
      }
      
      // For PagedOutput component, bind pages, currentPageIndex, and onChange from data
      if (component.type === 'PagedOutput') {
        if (data.content !== undefined) {
          (props as any).content = data.content;
        }
        if (data.pages !== undefined) {
          (props as any).pages = data.pages;
        }
        if (data.currentPageIndex !== undefined) {
          (props as any).currentPageIndex = data.currentPageIndex;
        }
        if (data.streamingInProgress !== undefined) {
          (props as any).streamingInProgress = data.streamingInProgress;
        }
        // Bind onPageChange handler (not onChange - that's for content editing)
        if (component.onChangeKey && data[component.onChangeKey]) {
          (props as any).onPageChange = data[component.onChangeKey];
        }
        // Bind onChange handler for content editing
        if (data.onContentChange) {
          (props as any).onChange = data.onContentChange;
        }
        // Add inpaint handler for Output nodes
        if (definition.type === 'Output') {
          // Add media metadata handler for aspect ratio detection
          (props as any).onMediaMetadataReady = handleMediaMetadataReady;
        }
        // Add video trim handler
        if (data.onVideoTrim) {
          (props as any).onVideoTrim = data.onVideoTrim;
        }
        // Add duplicate handler for Output nodes
        if (definition.type === 'Output') {
          (props as any).onDuplicateOutput = handleDuplicateOutput;
          (props as any).onPlaceFrameOnStage = handlePlaceFrameOnStage;
        }
        // Add condense handler for text content (exclude DocumentPanel from footer button only)
        if (data.onCondense && definition.type !== 'DocumentPanel') {
          (props as any).onCondense = data.onCondense;
        }
        // onCondenseRequest is for modal - should work for ALL nodes including DocumentPanel
        if (data.onCondenseRequest) {
          (props as any).onCondenseRequest = data.onCondenseRequest;
        }
        if (data.isCondensing !== undefined) {
          (props as any).isCondensing = data.isCondensing;
        }
        // Pass promptImprovementContext and nodeTitle for TextEditorModal
        // Determine content type based on node's outputsMediaType
        let contentType: 'image' | 'video' | 'text' = 'text';
        if (definition.outputsMediaType === 'image') {
          contentType = 'image';
        } else if (definition.outputsMediaType === 'video') {
          contentType = 'video';
        }
        (props as any).promptImprovementContext = {
          contentType,
          selectedModel: data.selectedModel || undefined,
          upstreamContext: data.inputContext || undefined,
          imageContext: data.imageContext || undefined,
          imageUrls: data.imageUrls || undefined,
          nodeParameters: (data.parameters as Record<string, any>) || undefined,
        };
        (props as any).nodeTitle = definition.title ? `Edit ${definition.title}` : 'Edit Prompt';
        // Use the node's API output label as the filename slug for downloads, when set
        if (typeof data.apiOutputLabel === 'string' && data.apiOutputLabel.trim()) {
          (props as any).outputLabel = data.apiOutputLabel;
        }
        // Add context menu handlers for fullscreen
        if (data.onSendToDevice) {
          (props as any).onSendToDevice = data.onSendToDevice;
        }
        if (data.onSetFlowThumbnail) {
          (props as any).onSetFlowThumbnail = data.onSetFlowThumbnail;
        }
        if (data.onDeleteOutput) {
          (props as any).onDeleteOutput = data.onDeleteOutput;
        }
        // Output Container nodes: enable context menu for media right-click actions
        if (definition.type === 'Output') {
          // Empty-state behavior: show drop zone if unconnected, blank if connected
          if ((data as any).outputEmptyBehavior) {
            (props as any).emptyBehavior = (data as any).outputEmptyBehavior;
          }
          if ((data as any).onUploadMedia) {
            (props as any).onUploadMedia = (data as any).onUploadMedia;
          }
        }
      }
      
      // For ImagePanel component, pass nodeId, isUploading, and context menu handlers
      if (component.type === 'ImagePanel') {
        (props as any).nodeId = id;
        (props as any).isUploading = data.isUploading || false;
        if (data.onSendToDevice) {
          (props as any).onSendToDevice = data.onSendToDevice;
        }
        if (data.onSetFlowThumbnail) {
          (props as any).onSetFlowThumbnail = data.onSetFlowThumbnail;
        }
      }
      
      // For AudioRecorder component, bind transcription handler and language
      if (component.type === 'AudioRecorder') {
        // Bind onTranscription handler (AudioRecorder uses this prop name, not onChange)
        if (component.onChangeKey && data[component.onChangeKey]) {
          (props as any).onTranscription = data[component.onChangeKey];
        }
        // Bind onRecordingComplete if available
        if (data.onRecordingComplete) {
          (props as any).onRecordingComplete = data.onRecordingComplete;
        }
        // Bind language from data
        if (data.language) {
          (props as any).language = data.language;
        }
        // Bind credit deduction callback
        if (data.onDeductCredits) {
          (props as any).onDeductCredits = data.onDeductCredits;
        }
      }
      
      // For TranscriptionOutput component, bind value and handlers
      if (component.type === 'TranscriptionOutput') {
        // Bind transcription value
        if (component.valueKey) {
          (props as any).value = data[component.valueKey] || '';
        }
        // Bind onChange handler for editing
        if (component.onChangeKey && data[component.onChangeKey]) {
          (props as any).onChange = data[component.onChangeKey];
        }
        // Bind onSubmit handler
        if (component.onSubmitKey && data[component.onSubmitKey]) {
          (props as any).onSubmit = data[component.onSubmitKey];
        }
        // Bind onCondense handler
        if (data.onCondense) {
          (props as any).onCondense = data.onCondense;
        }
        // Bind isCondensing state
        if (data.isCondensing !== undefined) {
          (props as any).isCondensing = data.isCondensing;
        }
      }
      
      // For PromptTemplate component, bind all necessary data
      if (component.type === 'PromptTemplate') {
        // Bind variables from data
        if (component.valueKey && data[component.valueKey] !== undefined) {
          (props as any).variables = data[component.valueKey];
        }
        // Bind onVariableChange if available
        if (data.onVariableChange) {
          (props as any).onVariableChange = data.onVariableChange;
        }
        // Bind parameters
        if (data.parameters !== undefined) {
          (props as any).parameters = data.parameters;
        }
        // Bind nodeId for context
        (props as any).nodeId = id;
        // Bind context if available
        if (data.context !== undefined) {
          (props as any).context = data.context;
        }
        // Bind threadId if available
        if (data.threadId !== undefined) {
          (props as any).threadId = data.threadId;
        }
        // Bind onExecute callback if available
        if (data.onExecute) {
          (props as any).onExecute = data.onExecute;
        }
        // Note: PromptTemplate doesn't use onSubmit - it uses onExecute instead
      }
      
      // For ParameterControls, bind values and onChange
      if (component.type === 'ParameterControls') {
        // Bind parameter values from data.parameters
        if (component.valueKey && data[component.valueKey] !== undefined) {
          (props as any).values = data[component.valueKey];
        }
        // Bind onChange handler
        if (component.onChangeKey && data[component.onChangeKey]) {
          (props as any).onChange = data[component.onChangeKey];
        }
        // Also bind modelSelector if present
        if ((props as any).modelSelector) {
          // IMPORTANT: Create a deep copy to avoid mutating the original definition
          // Shallow copy from line 192 means nested objects are still references
          (props as any).modelSelector = { ...(props as any).modelSelector };
          const modelSelectorConfig = (props as any).modelSelector;
          
          // Bind value: use data value if defined, otherwise use definition default
          if (modelSelectorConfig.valueKey) {
            const dataValue = data[modelSelectorConfig.valueKey];
            if (dataValue !== undefined) {
              // Use data value when present (including empty string, null, etc.)
              modelSelectorConfig.value = dataValue;
            } else {
              // Fallback to definition default only when data value is undefined
              const definitionDefault = component.props?.modelSelector?.value;
              if (definitionDefault) {
                modelSelectorConfig.value = definitionDefault;
              }
            }
          }
          
          // Bind onChange handler
          if (modelSelectorConfig.onChangeKey && data[modelSelectorConfig.onChangeKey]) {
            modelSelectorConfig.onChange = data[modelSelectorConfig.onChangeKey];
          }
        }
      }
      
      // Generate unique key by combining node id with component id to prevent duplicate key warnings
      const uniqueKey = `${id}-${component.id}`;
      return renderComponent(component.type, props as any, uniqueKey);
    });
  };

  // Input handles come from the registry — it is the single source of truth for
  // handle ids, and edges are validated against the very same function. Deriving
  // them here independently is what let Minimax advertise an `input-text` handle
  // the renderer never drew (VideoStitch clip counts and first-last-frame mode
  // are handled inside getInputHandles* too).
  const registryInputHandles = getInputHandlesForDefinitionType(definition.type, data);
  const multipleInputHandles = registryInputHandles.length > 1
    ? registryInputHandles.map(({ id, label, type }) => ({ id, label, type, position: undefined }))
    : undefined;

  // Custom header styling for migrated custom nodes
  const customHeaderStyle: React.CSSProperties = {};
  const customIcon = data._customNodeIcon as string | undefined;
  const customColor = data._customNodeColor as string | undefined;
  
  if (definition._customNodeMeta && customColor) {
    customHeaderStyle.backgroundColor = customColor;
  }

  // DocumentPanel nodes have no header buttons
  // API pill in the header actions (upper-right corner) with inline label to the left
  const apiPillButton = (() => {
    if (definition.type === 'Output') {
      return (
        <div className="api-pill-header-group">
          {!!data.apiOutput && (
            <input
              className="api-pill-header-label"
              value={(data.apiOutputLabel as string) || ''}
              onChange={(e) => handleApiOutputLabelChange(e.target.value)}
              placeholder="output_1"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          )}
          <button
            className={`api-pill-header-btn ${data.apiOutput ? 'api-pill-header-btn--active' : ''}`}
            onClick={handleToggleApiOutput}
            title={data.apiOutput ? 'API Output enabled — click to disable' : 'Mark as API output'}
          >
            ⚡
          </button>
        </div>
      );
    }
    if (definition.type === 'ImagePanel' || definition.type === 'DocumentPanel') {
      return (
        <div className="api-pill-header-group">
          {!!data.apiInput && (
            <input
              className="api-pill-header-label"
              value={(data.apiInputLabel as string) || ''}
              onChange={(e) => handleApiInputLabelChange(e.target.value)}
              placeholder="input_1"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          )}
          <button
            className={`api-pill-header-btn ${data.apiInput ? 'api-pill-header-btn--active' : ''}`}
            onClick={handleToggleApiInput}
            title={data.apiInput ? 'API Input enabled — click to disable' : 'Mark as API input'}
          >
            ⚡
          </button>
        </div>
      );
    }
    return null;
  })();

  const headerButtons = (definition.type === 'DocumentPanel' && !apiPillButton) ? null : apiPillButton;

  // Determine if we should lock aspect ratio (for Output nodes with image/video content, or ImagePanel with image)
  const currentAspectRatio = data.currentAspectRatio as number | undefined;
  const currentType = data.type as string | undefined;
  const preloadedWidth = data.preloadedWidth as number | undefined;
  const preloadedHeight = data.preloadedHeight as number | undefined;
  
  // For ImagePanel, calculate aspect ratio from preloaded dimensions
  const imagePanelAspectRatio = (preloadedWidth && preloadedHeight) 
    ? preloadedWidth / preloadedHeight 
    : undefined;
  
  const shouldKeepAspectRatio = 
    // Output nodes with image/video content
    (definition.type === 'Output' && 
      (currentType === 'image' || currentType === 'video') && 
      currentAspectRatio !== undefined && 
      currentAspectRatio > 0) ||
    // ImagePanel nodes with an image loaded
    (definition.type === 'ImagePanel' && 
      data.imageUrl && 
      imagePanelAspectRatio !== undefined && 
      imagePanelAspectRatio > 0);

  // A saved node still pinning a retired / retiring model (the Gemini Omni
  // preview, shut down 2026-09-30) says so in its status bar instead of failing
  // generically on Send. Not while a generation is running.
  const retiredModelNotice = data.status === 'processing' || data.isProcessing
    ? undefined
    : geminiOmniPreviewNotice(data.selectedModel);

  return (
    <>
      <BaseNode
        id={id}
        selected={selected}
        title={definition.title}
        variant={definition.variant}
        outputType={(() => {
          if (definition.type === 'Output' && data.type) {
            const t = data.type as string;
            if (t === 'text' || t === 'image' || t === 'video' || t === 'audio') return t;
            return undefined;
          }
          return definition.outputsMediaType as 'text' | 'image' | 'video' | 'audio' | undefined;
        })()}
        status={retiredModelNotice ? (retiredModelNotice.retired ? 'error' : 'warning') : data.status as 'idle' | 'processing' | 'success' | 'error' | 'warning' | undefined}
        statusMessage={retiredModelNotice ? retiredModelNotice.message : data.statusMessage as string | undefined}
        hasInputHandle={!multipleInputHandles && (definition.hasInputHandle ?? true)}
        hasOutputHandle={definition.hasOutputHandle ?? true}
        inputLabel={(data.type ? String(data.type).toUpperCase() : undefined) || (definition.acceptsMediaTypes?.length === 1 ? definition.acceptsMediaTypes[0].toUpperCase() : 'ANY')}
        outputLabel={(data.type ? String(data.type).toUpperCase() : undefined) || (definition.outputsMediaType && definition.outputsMediaType !== 'any' ? definition.outputsMediaType.toUpperCase() : 'ANY')}
        multipleInputHandles={multipleInputHandles}
        headerButtons={headerButtons}
        onDelete={data.onDelete as ((id: string) => void) | undefined}
        onDuplicate={data.onDuplicate as ((id: string) => void) | undefined}
        headerStyle={Object.keys(customHeaderStyle).length > 0 ? customHeaderStyle : undefined}
        customIcon={customIcon}
        keepAspectRatio={shouldKeepAspectRatio as boolean | undefined}
      >
        {/* File Context Indicator */}
        {data.fileContexts && Array.isArray(data.fileContexts) && data.fileContexts.length > 0 ? (
          <FileContextIndicator
            fileCount={data.fileContexts.length}
            files={data.fileContexts as any}
            variant={definition.variant}
          />
        ) : null}
        
        {/* Content wrapper with loading overlay for generator nodes */}
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {/* Loading overlay for generator nodes (not output nodes) */}
          {definition.category !== 'output' && (
            <LoadingOverlay
              isProcessing={!!data.isProcessing}
              nodeId={id}
              outputType={definition.outputsMediaType}
              estimatedDuration={data.estimatedDuration as number | undefined}
            />
          )}
          
          {/* Wrap content in FileDropZone if handler exists */}
          {data.onFilesDropped && typeof data.onFilesDropped === 'function' ? (
            <FileDropZone onFilesDropped={data.onFilesDropped as any}>
              {renderNodeComponents()}
            </FileDropZone>
          ) : (
            renderNodeComponents()
          )}
          
          {/* Spacer for utility nodes with no content components — keeps the settings panel
              below the labeled input handles (which are at ~128px and +40px per handle).
              Without this, the settings footer starts at the top of the content area and
              its onMouseDown stopPropagation blocks handle interactions.
              Also doubles as the output info display area for VideoStitch. */}
          {alwaysShowSettings && definition.components.length === 0 && (
            <div
              style={{
                minHeight: `${80 + (multipleInputHandles ? multipleInputHandles.length * 40 : 0)}px`,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                paddingBottom: '10px',
                gap: '2px',
              }}
            >
              {definition.type === 'ImageScale' ? (
                <div
                  style={{
                    fontSize: '11px',
                    color: 'rgba(255,255,255,0.4)',
                    letterSpacing: '0.05em',
                    fontVariantNumeric: 'tabular-nums',
                    pointerEvents: 'none',
                    textAlign: 'center',
                    lineHeight: 1.6,
                  }}
                >
                  {!!(data.inputWidth && data.inputHeight) && (
                    <div>Input: {String(data.inputWidth)}×{String(data.inputHeight)}</div>
                  )}
                  {(() => {
                    const params = (data.parameters || {}) as Record<string, any>;
                    const tw = Number(params.targetWidth) || 0;
                    const th = Number(params.targetHeight) || 0;
                    if (tw > 0 && th > 0) {
                      return <div>Output: {tw}×{th}</div>;
                    }
                    return data.outputDimensions ? <div>Output: {String(data.outputDimensions)}</div> : null;
                  })()}
                </div>
              ) : (
                !!data.outputDimensions && (
                  <div
                    style={{
                      fontSize: '11px',
                      color: 'rgba(255,255,255,0.4)',
                      letterSpacing: '0.05em',
                      fontVariantNumeric: 'tabular-nums',
                      pointerEvents: 'none',
                    }}
                  >
                    {String(data.outputDimensions)}
                  </div>
                )
              )}
            </div>
          )}

          {/* Video Timeline Preview — for VideoTrim/VideoSplit with always-visible settings */}
          {alwaysShowSettings && (definition.type === 'VideoTrim' || definition.type === 'VideoSplit') && (
            <VideoTimelinePreview
              videoUrl={(data.connectedVideoUrl as string) || ''}
              mode={definition.type === 'VideoTrim' ? 'trim' : 'split'}
              startTime={((data.parameters as Record<string, any>)?.startTime as number) ?? 0}
              endTime={((data.parameters as Record<string, any>)?.endTime as number) ?? 5}
              onStartTimeChange={(t) => handleSettingsChange('startTime', t)}
              onEndTimeChange={(t) => handleSettingsChange('endTime', t)}
              splitMode={((data.parameters as Record<string, any>)?.splitMode as 'equal' | 'seconds') ?? 'equal'}
              numParts={((data.parameters as Record<string, any>)?.numParts as number) ?? 2}
              segmentDuration={((data.parameters as Record<string, any>)?.segmentDuration as number) ?? 3}
            />
          )}

          {/* Output dimensions badge — for Scale nodes after processing (not shown for VideoStitch which uses the spacer area above) */}
          {!!data.outputDimensions && definition.components.length > 0 && (
            <div
              style={{
                padding: '4px 12px',
                fontSize: '11px',
                color: 'rgba(255,255,255,0.5)',
                textAlign: 'right',
                letterSpacing: '0.02em',
                fontVariantNumeric: 'tabular-nums',
                pointerEvents: 'none',
              }}
            >
              Output: {String(data.outputDimensions)}
            </div>
          )}

          {/* Settings Footer - inline right after content */}
          {hasSettings && (
            <NodeSettingsFooter
              nodeId={id}
              isSettingsOpen={alwaysShowSettings ? true : (isSettingsOpen ?? false)}
              onSettingsClose={handleSettingsClose}
              hideCloseButton={alwaysShowSettings}
              color={getNodeTypeColor()}
              fields={definition.settings!}
              values={(data.parameters || {}) as Record<string, any>}
              onChange={handleSettingsChange}
              onGenerate={handleSettingsGenerate}
              onCancel={data.onCancelGeneration as (() => void) | undefined}
              showCancel={
                !!data.isProcessing &&
                (((data.parameters || {}) as Record<string, any>).batchCount ?? 1) > 1
              }
              creditCost={data.creditCost as number | undefined}
              usesOwnKey={!!data.usesOwnKey}
              isProcessing={!!data.isProcessing}
              zIndex={getNode(id)?.zIndex ?? 0}
            />
          )}
        </div>
        
        {/* Settings labels when closed - show selected option labels in a vertical list */}
        {hasSettings && !isSettingsOpen && (
          <div 
            className="node-settings-labels"
            style={{
              position: 'absolute',
              bottom: '72px',
              right: '48px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              alignItems: 'flex-end',
              pointerEvents: 'none',
              zIndex: 5,
              padding: '6px 8px',
              background: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '4px',
            }}
          >
            {getSelectedSettingsLabels().map((label, index) => (
              <span
                key={index}
                style={{
                  fontSize: '11px',
                  color: 'var(--textarea-text-color, #A2A2A2)',
                  opacity: 0.7,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  maxWidth: '150px',
                }}
              >
                {label}
              </span>
            ))}
            {/* Arrow pointing to settings button - aligned with first line at bottom */}
            <div
              style={{
                position: 'absolute',
                right: '-6px',
                bottom: '6px',
                width: 0,
                height: 0,
                borderTop: '6px solid transparent',
                borderBottom: '6px solid transparent',
                borderLeft: '6px solid rgba(0, 0, 0, 0.5)',
              }}
            />
          </div>
        )}
      </BaseNode>

      {/* Inpaint Editor Dialog - for Output nodes with images */}
      {inpaintOpen && inpaintImageUrl && (
        <InpaintEditor
          imageUrl={inpaintImageUrl}
          onClose={() => setInpaintOpen(false)}
          onAccept={handleInpaintAccept}
        />
      )}
    </>
  );
};

/**
 * Memoized UniversalNode to prevent re-renders when other nodes change
 * Only re-renders when id, data, or selected props change
 */
export const UniversalNode = memo(UniversalNodeComponent, (prevProps, nextProps) => {
  // Custom comparison: only re-render if these specific props change
  return (
    prevProps.id === nextProps.id &&
    prevProps.selected === nextProps.selected &&
    prevProps.data === nextProps.data
  );
});
